/** MobileBottomPanel – mobile-only bottom navigation bar with slide-out drawers. */
import React, {useEffect, useState} from "react";
import {Box, Button, Drawer, IconButton} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {IncomingMessageBox} from "../IncomingMessageBox/IncomingMessageBox";
import {InteractionInterfaceBox} from "../InteractionInterfaceBox/InteractionInterfaceBox";
import {HistoryItem} from "../../types/history";
import styles from "./MobileBottomPanel.module.scss";

/* ─────────────────────────────────────────────
   Tab IDs  – never change these values;
   they only control which drawer is open.
───────────────────────────────────────────── */
type PanelId = "left" | "right" | null;

// Left panel contains G + S boxes.  Right panel contains I box.
const LEFT_TABS = ["S"] as const;
const RIGHT_TABS = ["I"] as const;

type TabKey = typeof LEFT_TABS[number] | typeof RIGHT_TABS[number];

const TAB_META: Record<TabKey, { icon: React.ReactNode; label: string, selector: string }> = {
    // G: { icon: <ShieldOutlinedIcon     sx={{ fontSize: 22 }} />, label: "Guardian" ,selector:"openMessageBoxButton"},
    S: {icon: <ChatBubbleOutlineIcon sx={{fontSize: 22}}/>, label: "System", selector: "openMessageBoxButton"},
    I: {icon: <TimelineOutlinedIcon sx={{fontSize: 22}}/>, label: "Interaction", selector: "openInteractionBoxButton"},
};

const TAB_ORDER: TabKey[] = [
    // "G",
    "S", "I"];

/* ─────────────────────────────────────────────────── */

type Props = {
    selectedGuardian: any | null;
    history: HistoryItem[];
};

export const MobileBottomPanel: React.FC<Props> = ({selectedGuardian, history}) => {
    // activeTab  = which bottom-bar button is highlighted
    // openPanel  = which drawer is open ("left" | "right" | null)
    const [activeTab, setActiveTab] = useState<TabKey | null>(null);
    const [openPanel, setOpenPanel] = useState<PanelId>(null);

    /* ── clicking a tab ──────────────────────────────── */
    const handleTabClick = (tab: TabKey) => {
        const targetPanel: PanelId = LEFT_TABS.includes(tab as any) ? "left" : "right";

        if (activeTab === tab) {
            // Same tab clicked again → close
            setActiveTab(null);
            setOpenPanel(null);
        } else {
            setActiveTab(tab);
            setOpenPanel(targetPanel);
        }
    };

    /* ── close any open drawer ───────────────────────── */
    const closeDrawer = () => {
        setActiveTab(null);
        setOpenPanel(null);
    };

    const outSideLeftRef = React.useRef<HTMLButtonElement>(null);
    const outSideRightRef = React.useRef<HTMLButtonElement>(null);
    const drawerLeftRef = React.useRef<any>(null);
    const drawerRightRef = React.useRef<any>(null);

    useEffect(() => {
        const updateOutsideSize = () => {
            const height = outSideLeftRef.current?.offsetHeight || 0;
            const width = outSideLeftRef.current?.offsetWidth || 0;
            console.log(outSideLeftRef);
            document.documentElement.style.setProperty(
                "--drawer-left-width",
                `${width}px`
            );
            document.documentElement.style.setProperty(
                "--drawer-left-height",
                `${height}px`
            );

            console.log("--drawer-left-width", width)
        };

        updateOutsideSize(); // lần đầu

        window.addEventListener("resize", updateOutsideSize);

        return () => {
            window.removeEventListener("resize", updateOutsideSize);
        };
    }, []);

    /* ─────────────────────────────────────────────────── */
    return (
        <>
            {/* ══════════════════════════════════════════
                LEFT DRAWER  (Guardian + System boxes)
                anchor = "left"
            ══════════════════════════════════════════ */}
            <Button className={`outside-messagebox-button ${styles.outsideMessageboxButton}`}>Click me</Button>
            <Button className={`outside-historybox-button ${styles.outsideHistoryboxButton}`}>Click me</Button>

            <Drawer
                anchor="left"
                variant="temporary"
                open={openPanel === "left"}
                onClose={closeDrawer}
                ModalProps={{keepMounted: true}}
                className={'mobile-message-drawer'}
                sx={{
                    display: {xs: "block", md: "none"},
                    "& .MuiDrawer-paper": {
                        width: "85vw",
                        maxWidth: 360,
                        display: "flex",
                        flexDirection: "column",
                        background: "linear-gradient(180deg, #3d4a2d 0%, #2d3622 100%)",
                        borderRight: "1px solid rgba(255,255,255,0.1)",
                        // bottom bar is hidden; no padding needed
                        paddingBottom: "0px",
                    },
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.45)",
                    },
                }}
            >
                {/* Header */}
                <Box className={`${styles.drawerHeader}`}>
                    <span className={styles.drawerHeaderTitle}>Guardian &amp; System</span>
                    <IconButton size="small" onClick={closeDrawer}
                                sx={{color: "rgba(255,255,255,0.55)", "&:hover": {color: "#fff"}}}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Box>

                {/* Content — IncomingMessageBox renders BOTH Guardian + System sections */}
                <Box className={`${styles.drawerContent} MobileBottomPanel`}
                     ref={drawerLeftRef}
                >
                    <IncomingMessageBox selectedGuardian={selectedGuardian}/>
                </Box>
            </Drawer>

            {/* ══════════════════════════════════════════
                RIGHT DRAWER  (Interaction Interface)
                anchor = "right"
            ══════════════════════════════════════════ */}
            <Drawer
                anchor="right"
                variant="temporary"
                open={openPanel === "right"}
                ref={drawerRightRef}
                onClose={closeDrawer}
                ModalProps={{keepMounted: true}}
                sx={{
                    display: {xs: "block", md: "none"},
                    "& .MuiDrawer-paper": {
                        width: "85vw",
                        maxWidth: 360,
                        display: "flex",
                        flexDirection: "column",
                        background: "linear-gradient(180deg, #3d4a2d 0%, #2d3622 100%)",
                        borderLeft: "1px solid rgba(255,255,255,0.1)",
                        paddingBottom: "0px",
                        backgroundColor: 'rgb(255 255 255 / 27%)'
                    },
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.45)",
                    },
                }}
            >
                {/* Header */}
                <Box className={styles.drawerHeader}>
                    <span className={styles.drawerHeaderTitle}>Interaction</span>
                    <IconButton size="small" onClick={closeDrawer}
                                sx={{color: "rgba(255,255,255,0.55)", "&:hover": {color: "#fff"}}}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Box>

                {/* Content */}
                <Box className={`${styles.drawerContent} MobileBottomPanel`}>
                    <InteractionInterfaceBox history={history}/>
                </Box>
            </Drawer>

            {/* ══════════════════════════════════════════
                FIXED BOTTOM NAVIGATION BAR
                Only visible on mobile (xs / < md)
            ══════════════════════════════════════════ */}
            <Box
                sx={{display: {xs: "flex", md: "none"}}}
                className={styles.bottomBar}
                role="navigation"
                aria-label="Mobile panel navigation"
            >
                {TAB_ORDER.map((tab) => (
                    <button
                        key={tab}
                        className={`${styles.tabBtn}${activeTab === tab ? ` ${styles.active}` : ""} ${TAB_META[tab].selector}`}
                        onClick={() => handleTabClick(tab)}
                        aria-label={TAB_META[tab].label}
                        aria-pressed={activeTab === tab}
                    >
                        {TAB_META[tab].icon}
                        <span className={styles.tabLabel}>{tab}</span>
                    </button>
                ))}
            </Box>
        </>
    );
};

